import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, Mail } from 'lucide-react';
import { useAuth } from '../components/auth/AuthContext';
import AuthSplit from '../components/auth/AuthSplit';

const ForgotPassword: React.FC = () => {
  const { resetPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await resetPassword(email);
      setSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send the reset email.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthSplit
      eyebrow="Student account / Account recovery"
      title="Recover your account."
      lede="Enter the email on your student account and we will send a secure link to choose a new password."
      notes={
        <div className="space-y-2.5">
          <p>The link in the email is the only key. Open it in this browser and choose a password of at least 8 characters.</p>
          <p>If the address is wrong, the recovery email will not arrive. Contact the student desk if you no longer use the address on account.</p>
        </div>
      }
      footer={
        <Link to="/login" className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-blue-700 hover:text-blue-800">
          <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.8} /> Back to sign in
        </Link>
      }
    >
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-[0_24px_70px_-48px_rgba(15,40,90,0.45)] sm:p-7">
        <div className="flex items-center justify-between gap-3 border-b border-slate-100 pb-4">
          <h2 className="text-[17px] font-semibold tracking-[-0.015em] text-slate-950">Password recovery</h2>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[9px] font-semibold uppercase tracking-[0.1em] text-slate-500">
            {sent ? 'Link sent' : 'Step 1 of 2'}
          </span>
        </div>

        {sent ? (
          <div className="mt-5 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-[12.5px] leading-5 text-emerald-800">
            <div className="flex items-start gap-2.5">
              <CheckCircle2 className="mt-0.5 h-5 w-5 flex-none" strokeWidth={1.6} />
              <div>
                <p className="font-semibold">Reset link sent</p>
                <p className="mt-1">
                  Check <strong className="font-semibold">{email}</strong> and follow the link. It expires after a
                  short while. Check spam if it does not arrive.
                </p>
                <button type="button" onClick={() => setSent(false)} className="mt-3 font-semibold underline underline-offset-2">
                  Use a different email
                </button>
              </div>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-5 space-y-4">
            {error && (
              <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-[12.5px] leading-5 text-red-700" role="alert">
                {error}
              </div>
            )}
            <div>
              <label htmlFor="recovery-email" className="auth-label">Email on account</label>
              <div className="relative">
                <Mail className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" strokeWidth={1.7} />
                <input
                  id="recovery-email"
                  type="email"
                  required
                  autoComplete="email"
                  placeholder="you@example.com"
                  className="auth-field pl-11"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            </div>
            <button type="submit" disabled={busy} className="auth-btn-primary">
              {busy ? 'Sending…' : 'Send reset link'}
            </button>
          </form>
        )}

        <p className="mt-5 text-[11.5px] leading-5 text-slate-500">
          The student desk can be reached at{' '}
          <a href="mailto:info@lsem.org.ng" className="auth-link">
            info@lsem.org.ng
          </a>
          .
        </p>
      </div>
    </AuthSplit>
  );
};

export default ForgotPassword;
